export type CornerPoint = { x: number; y: number };

export type DetectedQrCode = {
    rawValue: string;
    cornerPoints: CornerPoint[];
};

export type QrDetectionEvaluation = {
    currentCodeOutOfFrame: boolean;
    scannableCodes: DetectedQrCode[];
};

/** QRスキャン値から前後の不可視文字（CR, LF, 半角スペース, 全角スペース）を除去する */
export const sanitizeQrCode = (value: string): string => value.replace(/^[\s\u3000]+|[\s\u3000]+$/g, '');

export const calculateQrCenter = (cornerPoints: CornerPoint[]): CornerPoint => {
    const sumX = cornerPoints.reduce((sum, p) => sum + p.x, 0);
    const sumY = cornerPoints.reduce((sum, p) => sum + p.y, 0);
    return {
        x: sumX / cornerPoints.length,
        y: sumY / cornerPoints.length,
    };
};

export const calculateDistanceFromCenter = (
    cornerPoints: CornerPoint[],
    viewWidth: number,
    viewHeight: number
): number => {
    const qrCenter = calculateQrCenter(cornerPoints);
    const frameCenterX = viewWidth / 2;
    const frameCenterY = viewHeight / 2;
    return Math.sqrt((qrCenter.x - frameCenterX) ** 2 + (qrCenter.y - frameCenterY) ** 2);
};

export const isInsideScanFrame = (
    cornerPoints: CornerPoint[],
    viewWidth: number,
    viewHeight: number,
    frameSize: number
): boolean => {
    if (cornerPoints.length === 0) return false;

    const frameCenterX = viewWidth / 2;
    const frameCenterY = viewHeight / 2;

    const qrCenter = calculateQrCenter(cornerPoints);

    // MEMO: 読み取り範囲を白枠から狭めて、なるべくフレーム中央のQRのみ読み込めるようにする仕掛け
    const maxDistance = frameSize / 2;
    const distanceX = Math.abs(qrCenter.x - frameCenterX);
    const distanceY = Math.abs(qrCenter.y - frameCenterY);

    return distanceX <= maxDistance && distanceY <= maxDistance;
};

export const evaluateQrDetection = (
    detectedCodes: DetectedQrCode[],
    currentQrCode: string | null | undefined,
    scannedQrCodes: readonly string[],
    viewWidth: number,
    viewHeight: number,
    frameSize: number
): QrDetectionEvaluation => {
    const codes = detectedCodes.map((code) => ({
        ...code,
        rawValue: sanitizeQrCode(code.rawValue),
    }));
    const currentCode = codes.find((code) => code.rawValue === currentQrCode);
    const excludedQrCodes = new Set([...(currentQrCode ? [currentQrCode] : []), ...scannedQrCodes]);

    return {
        currentCodeOutOfFrame:
            currentQrCode !== null &&
            currentQrCode !== undefined &&
            (!currentCode || !isInsideScanFrame(currentCode.cornerPoints, viewWidth, viewHeight, frameSize)),
        scannableCodes: codes.filter(
            (code) =>
                code.rawValue.length > 0 &&
                !excludedQrCodes.has(code.rawValue) &&
                isInsideScanFrame(code.cornerPoints, viewWidth, viewHeight, frameSize)
        ),
    };
};
